"use client"

import { ScrollTrigger, gsap } from "gsap/all"
import Lenis, { type LenisOptions } from "lenis"
import { useEffect, useState } from "react"
import { useDeepCompareLayoutEffect } from "use-deep-compare"
import TypedEventEmitter from "./TypedEventEmitter"
import { isBrowser } from "./deviceDetection"
import { GlobalStyles, css, unresponsive } from "./styled"

const styles = unresponsive(css`
	html.lenis,
	html.lenis body {
		height: auto;
	}

	.lenis.lenis-smooth [data-lenis-prevent] {
		overscroll-behavior: contain;
	}

	.lenis.lenis-stopped {
		body {
			overflow: clip;
		}
	}

	.lenis.lenis-smooth iframe {
		pointer-events: none;
	}
`)

const locks: symbol[] = []
const locksChange = new TypedEventEmitter<{ change: [] }>()

export const refreshScrollLocks = () => {
	locksChange.dispatchEvent("change")
}

/**
 * lock and unlock the scroller, without interfering with other tools that also lock the scroller
 *
 * you can can either lock the scrolling
 * or force to unlock scrolling (even if locks exist)
 */
export const createScrollLock = (type: "lock" | "unlock" = "lock") => {
	const lockId = Symbol(`scroll-${type}`)

	locks.push(lockId)
	locksChange.dispatchEvent("change")

	return {
		/**
		 * you can call this multiple times without issue if it's more convenient
		 */
		release: () => {
			const index = locks.indexOf(lockId)

			if (index >= 0) {
				locks.splice(index, 1)
				locksChange.dispatchEvent("change")
			}
		},
	}
}

/**
 * lock and unlock the scroller, without interfering with other tools that also lock the scroller
 * plus some react state sugar for scroll locking to make it easier to use
 * locks are also automatically released on unmount
 *
 * you can can either lock the scrolling
 * or force to unlock scrolling (even if locks exist)
 *
 * you can also set the value via the second argument if you have external state
 */
export const useScrollLock = (
	type: "lock" | "unlock" = "lock",
	value?: boolean,
) => {
	const [locked, setLocked] = useState(false)
	const shouldLock = value ?? locked

	useEffect(() => {
		if (shouldLock) {
			const lock = createScrollLock(type)

			return () => lock.release()
		}
	}, [type, shouldLock])

	return [locked, setLocked] as const
}

/**
 * shorthand for pins, which selects "fixed" if scroller is off and "transform"
 * if scroller is on
 */
export const usePinType = () => {
	const isSmooth = useIsSmooth()
	return isSmooth ? "transform" : "fixed"
}

/**
 * returns true if scroll smoothing is enabled
 */
export const useIsSmooth = () => {
	const [smooth, setSmooth] = useState(
		typeof window !== "undefined" &&
			window.matchMedia("(hover: hover)").matches,
	)

	useEffect(() => {
		const enableSmooth = () => {
			setSmooth(true)
		}
		const disableSmooth = () => {
			setSmooth(!!window.lenis?.options.syncTouch)
		}

		window.addEventListener("wheel", enableSmooth, { passive: true })
		window.addEventListener("touchstart", disableSmooth, { passive: true })

		return () => {
			window.removeEventListener("wheel", enableSmooth)
			window.removeEventListener("touchstart", disableSmooth)
		}
	}, [])

	// if the device is mobile, set the initial value to false
	useEffect(() => {
		const hover = window.matchMedia("(hover: hover)")
		if (!hover.matches) {
			setSmooth(false)
		}
	}, [])

	// check for url flags
	if (isBrowser && window.location.search.toLowerCase().includes("nosmooth"))
		return false
	if (isBrowser && window.location.search.toLowerCase().includes("forcesmooth"))
		return true

	return smooth
}

declare global {
	interface Window {
		lenis?: Lenis
	}
}

ScrollTrigger.config({
	ignoreMobileResize: true,
})

export const SmoothScrollStyle = ({ ...config }: LenisOptions = {}) => {
	useDeepCompareLayoutEffect(() => {
		/**
		 * create the smoother
		 */
		window.lenis?.destroy()

		// allow scrolling in error modals, chrome extensions, sanity studio, etc.
		// i'd like to clean this up a bit, but ok for now
		const rootLayout = document.querySelector(".root-layout")
		if (!rootLayout) throw new Error("root-layout not found")

		// Initialize a new Lenis instance for smooth scrolling
		const lenis = new Lenis({ ...config, eventsTarget: rootLayout })
		window.lenis = lenis

		// Synchronize Lenis scrolling with GSAP's ScrollTrigger plugin
		lenis.on("scroll", ScrollTrigger.update)

		// Add Lenis's requestAnimationFrame (raf) method to GSAP's ticker
		// This ensures Lenis's smooth scroll animation updates on each GSAP tick
		gsap.ticker.add((time) => {
			lenis.raf(time * 1000) // Convert time from seconds to milliseconds
		})

		// Disable lag smoothing in GSAP to prevent any delay in scroll animations
		gsap.ticker.lagSmoothing(0)

		// refresh on resize
		let needsRefresh = false
		let isMounted = true
		const onResize = () => {
			needsRefresh = true
		}
		const check = () => {
			if (!isMounted) return
			if (needsRefresh && lenis.velocity === 0) {
				needsRefresh = false
				ScrollTrigger.refresh()
			}
			requestAnimationFrame(check)
		}
		requestAnimationFrame(check)

		/**
		 * pull state from the scroll locks
		 */
		const onChange = () => {
			const unlockers = locks.find(
				(lock) => lock.description === "scroll-unlock",
			)
			const lockers = locks.find((lock) => lock.description === "scroll-lock")

			if (unlockers) lenis.start()
			else if (lockers) lenis.stop()
			else lenis.start()
		}

		onChange()

		locksChange.addEventListener("change", onChange)
		window.addEventListener("resize", onResize)
		return () => {
			isMounted = false
			locksChange.removeEventListener("change", onChange)
			window.removeEventListener("resize", onResize)
		}
	}, [config])

	return <GlobalStyles>{styles}</GlobalStyles>
}
